import { Box, IconButton, Stack, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { CRITERION_OPERATORS, type CriterionOperator, type MeterInput, type NewMeter } from '@vy/types';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { CriterionSelect } from '../../CriterionSelect';
import { MeterFieldSelect } from '../../MeterFieldSelect';
import { MeterFieldValueSelect } from '../../MeterFieldValueSelect';

type CriterionItemProps = {
  conditionIndex: number;
  criterionIndex: number;
  onCriterionDelete: (index: number) => void;
};

export const CriterionItem: React.FC<CriterionItemProps> = ({
  conditionIndex,
  criterionIndex,
  onCriterionDelete,
}) => {
  const { control, setValue } = useFormContext<MeterInput, unknown, NewMeter>();

  const eventName = useWatch({
    control,
    name: 'eventName',
  });

  const criterion = useWatch({
    control,
    name: `filter.conditions.${conditionIndex}.criterions.${criterionIndex}`,
  });

  const handleDeleteButtonClick = () => {
    onCriterionDelete(criterionIndex);
  };

  const isUnaryOperator = (operator: CriterionOperator) => {
    switch (operator) {
      case CRITERION_OPERATORS.Exists:
      case CRITERION_OPERATORS.DoesntExists:
        return true;
      default:
        return false;
    }
  };

  return (
    <Stack key={criterionIndex} direction="row" sx={{ columnGap: '12px' }}>
      {criterionIndex === 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'end',
            marginBottom: '8px',
            width: '54px',
            columnGap: 0.5,
          }}
        >
          <Typography variant="subtitle1" sx={{ display: 'inline' }}>
            When
          </Typography>
        </Box>
      )}
      {criterionIndex > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'end',
            marginBottom: '8px',
            width: '54px',
          }}
        >
          <Typography variant="subtitle1" color="secondary">
            And
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        <Controller
          control={control}
          name={`filter.conditions.${conditionIndex}.criterions.${criterionIndex}.field`}
          render={({ field }) => (
            <MeterFieldSelect
              eventName={eventName}
              value={field.value}
              onChange={(event, newValue) => {
                event.preventDefault();
                field.onChange(newValue);
              }}
              inputRef={field.ref}
            />
          )}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Controller
          control={control}
          name={`filter.conditions.${conditionIndex}.criterions.${criterionIndex}.operator`}
          render={({ field }) => (
            <CriterionSelect
              value={field.value}
              onChange={(event, newValue) => {
                event.preventDefault();

                const prevAtomic = isUnaryOperator(field.value);

                if (isUnaryOperator(newValue as CriterionOperator)) {
                  setValue(
                    `filter.conditions.${conditionIndex}.criterions.${criterionIndex}.value`,
                    newValue ?? 'Any',
                  );
                } else if (prevAtomic) {
                  setValue(`filter.conditions.${conditionIndex}.criterions.${criterionIndex}.value`, '');
                }

                field.onChange(newValue);
              }}
              inputRef={field.ref}
            />
          )}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Controller
          control={control}
          name={`filter.conditions.${conditionIndex}.criterions.${criterionIndex}.value`}
          render={({ field }) => (
            <MeterFieldValueSelect
              sx={{
                display: isUnaryOperator(criterion.operator) ? 'none' : 'block',
              }}
              eventName={eventName}
              fieldName={criterion.field}
              value={field.value}
              onChange={(event, newValue) => {
                event.preventDefault();
                field.onChange(newValue);
              }}
              inputRef={field.ref}
            />
          )}
        />
      </Box>
      <Box
        sx={{
          alignItems: 'end',
          display: 'flex',
          marginBottom: '2px',
        }}
      >
        <IconButton onClick={handleDeleteButtonClick}>
          <VyIcon icon="Delete" />
        </IconButton>
      </Box>
    </Stack>
  );
};
